/**
 * Procesa la respuesta de una petición fetch compatible con el backend refactorizado.
 * @param {Response} response - El objeto de respuesta de fetch.
 * @returns {Promise<Object>} - Un objeto con { success, data, message } o lanza error.
 */
export const handleResponse = async (response) => {
  try {
    // Intentar parsear JSON siempre
    const responseBody = await response.json();
    
    if (!response.ok) {
      // Manejar errores HTTP con estructura del backend refactorizado
      const errorMessage = responseBody.error || 
                          responseBody.message || 
                          `Error del servidor: ${response.status}`;
      
      // Mantener detalles adicionales si existen
      const error = new Error(errorMessage);
      error.details = responseBody.details;
      error.statusCode = response.status;
      
      throw error;
    }
    
    // Verificar si es el formato nuevo del backend refactorizado
    if (responseBody.hasOwnProperty('success')) {
      if (responseBody.success) {
        // Respuesta exitosa: {success: true, data: {...}, message: "..."}
        return {
          success: true,
          data: responseBody.data,
          message: responseBody.message
        };
      } else {
        // Respuesta de error: {success: false, error: "...", details: [...]}
        const errorMessage = responseBody.error || 'Error desconocido del servidor';
        const error = new Error(errorMessage);
        error.details = responseBody.details;
        throw error;
      }
    }
    
    // Compatibilidad hacia atrás para respuestas legacy
    return {
      success: true,
      data: responseBody,
      message: 'Operación exitosa'
    };
    
  } catch (jsonError) {
    // Si no se puede parsear JSON
    if (!response.ok) {
      throw new Error(`Error del servidor: ${response.status} - ${response.statusText}`);
    }
    
    // Re-lanzar errores ya procesados
    if (jsonError instanceof Error && jsonError.message !== 'Unexpected end of JSON input') {
      throw jsonError;
    }
    
    // Error de parsing JSON en respuesta exitosa
    throw new Error('Respuesta del servidor no válida');
  }
};


/**
 * Wrapper para fetch que incluye el token de autenticación y procesa la respuesta.
 * @param {string} url - La URL del endpoint.
 * @param {object} options - Las opciones para la petición fetch.
 * @param {string} token - El token de autenticación.
 * @returns {Promise<Object>} - La respuesta procesada con { success, data, message }.
 */
export const fetchWithAuth = async (url, options = {}, token) => {
  // Obtener token del localStorage si no se proporciona
  const authToken = token || localStorage.getItem('token');
  
  const headers = {
    ...options.headers,
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const newOptions = { ...options, headers };

  try {
    const response = await fetch(url, newOptions);
    return await handleResponse(response);
  } catch (error) {
    console.error('Error en fetchWithAuth:', error);
    throw error;
  }
}; 